import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FolderKanban, 
  ListTodo, 
  GanttChart, 
  Users, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Moon,
  Sun,
  Building2,
  Tags as TagsIcon,
  Check,
  Briefcase,
  BarChart3,
  AlertTriangle,
  Calendar,
  Clock,
  ArrowLeft,
  Target,
  Globe,
  Gauge
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { useTheme, colorThemes } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import { useOrg } from '../context/OrgContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';

const Sidebar = ({ currentUser, moduleType = 'project' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, colorTheme, changeColorTheme, currentColorTheme } = useTheme();
  const { logout } = useAuth();
  const { collapsed, setCollapsed } = useSidebar();
  const { organization, entities, selectedEntity, selectEntity } = useOrg();

  // Menu items for Project Management
  const projectMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', gradientKey: 'dashboard' },
    { icon: FolderKanban, label: 'Projects', path: '/projects', gradientKey: 'projects' },
    { icon: ListTodo, label: 'My Tasks', path: '/my-tasks', gradientKey: 'myTasks' },
    { icon: GanttChart, label: 'Timeline', path: '/timeline', gradientKey: 'timeline' },
    { icon: Building2, label: 'Companies', path: '/companies', gradientKey: 'companies' },
    { icon: TagsIcon, label: 'Sectors', path: '/sectors', gradientKey: 'tags' },
    { icon: Users, label: 'Team', path: '/team', gradientKey: 'team' },
  ];

  // Menu items for Portfolio Management
  const portfolioMenuItems = [
    { icon: BarChart3, label: 'Portfolio Overview', path: '/portfolio', gradientKey: 'dashboard' },
    { icon: AlertTriangle, label: 'Escalations', path: '/portfolio/escalations', gradientKey: 'projects' },
    { icon: Users, label: 'Project Managers', path: '/portfolio/pms', gradientKey: 'team' },
    { icon: Building2, label: 'By Company', path: '/portfolio/companies', gradientKey: 'companies' },
  ];

  // Menu items for Action Items Module - structured with settings icons
  const actionItemsMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/action-items', gradientKey: 'dashboard', exact: true },
    { icon: ListTodo, label: 'All', path: '/action-items/all', gradientKey: 'projects' },
    { icon: Briefcase, label: 'Meetings', path: '/action-items/meetings-view', gradientKey: 'team', hasSettings: true, settingsPath: '/action-items/manage-meetings' },
    { icon: Users, label: 'Lists', path: '/action-items/lists', gradientKey: 'companies', hasSettings: true, settingsPath: '/action-items/manage-lists' },
    { icon: FolderKanban, label: 'MOM', path: '/action-items/mom', gradientKey: 'tags' },
  ];

  // Menu items for Meeting Management Module
  const meetingMenuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/meetings', gradientKey: 'dashboard', exact: true },
    { icon: ListTodo, label: 'All Meetings', path: '/meetings/all', gradientKey: 'projects' },
    { icon: Calendar, label: 'Upcoming', path: '/meetings/upcoming', gradientKey: 'team' },
    { icon: Clock, label: 'Past', path: '/meetings/past', gradientKey: 'companies' },
    { icon: AlertTriangle, label: 'Pending Approval', path: '/meetings/pending-approval', gradientKey: 'myTasks' },
    { type: 'divider' },
    { icon: FolderKanban, label: 'Templates', path: '/meetings/templates', gradientKey: 'tags' },
  ];

  // Menu items for Strategy Execution Module
  const strategyMenuItems = [
    { icon: Target, label: 'Overview', path: '/strategy', gradientKey: 'dashboard', exact: true },
    { icon: Globe, label: 'Sectors', path: '/strategy/sectors', gradientKey: 'projects' },
    { icon: Building2, label: 'Companies', path: '/strategy/companies', gradientKey: 'team' },
    { icon: Settings, label: 'Functions', path: '/strategy/functions', gradientKey: 'companies' },
    { type: 'divider' },
    { icon: BarChart3, label: 'KPIs', path: '/strategy/kpis', gradientKey: 'myTasks' },
    { icon: Gauge, label: 'Dashboards', path: '/strategy/dashboards', gradientKey: 'tags' },
  ];

  // Menu items for Strategy Development Module
  const strategyDevMenuItems = [
    { icon: Target, label: 'All Strategies', path: '/strategy-development', gradientKey: 'dashboard', exact: true, entityAction: 'clear' },
  ];

  // Dynamically add entity items for strategy-development
  if (moduleType === 'strategy-development' && organization) {
    strategyDevMenuItems.push({ type: 'divider' });
    // Corporate
    strategyDevMenuItems.push({
      icon: Building2,
      label: organization.corporate_name || 'Corporate',
      path: '/strategy-development',
      gradientKey: 'projects',
      entityAction: 'select',
      entity: { id: 'corporate', name: organization.corporate_name, type: 'corporate' }
    });
    // Subsidiaries
    if (organization.subsidiaries?.length > 0) {
      strategyDevMenuItems.push({ type: 'header', label: 'Subsidiaries' });
      organization.subsidiaries.forEach((sub, i) => {
        strategyDevMenuItems.push({
          icon: Building2,
          label: sub.name,
          path: '/strategy-development',
          gradientKey: 'team',
          entityAction: 'select',
          entity: { id: `sub-${sub.id}`, name: sub.name, type: 'subsidiary' }
        });
      });
    }
    // Functions
    if (organization.functions?.length > 0) {
      strategyDevMenuItems.push({ type: 'header', label: 'Functions' });
      organization.functions.forEach((func, i) => {
        strategyDevMenuItems.push({
          icon: Settings,
          label: func.name,
          path: '/strategy-development',
          gradientKey: 'companies',
          entityAction: 'select',
          entity: { id: `func-${func.id}`, name: func.name, type: 'function' }
        });
      });
    }
  }

  // Select menu items based on module type
  const getMenuItems = () => {
    switch (moduleType) {
      case 'portfolio':
        return portfolioMenuItems;
      case 'action-items':
        return actionItemsMenuItems;
      case 'meeting-management':
        return meetingMenuItems;
      case 'strategy':
        return strategyMenuItems;
      case 'strategy-development':
        return strategyDevMenuItems;
      default:
        return projectMenuItems;
    }
  };
  const menuItems = getMenuItems();
  
  // Module styling based on type - matching ModuleSelector gradients
  const getModuleConfig = () => {
    switch (moduleType) {
      case 'portfolio':
        return {
          coloredWord: 'Portfolio',
          restOfName: 'Management',
          titleGradient: 'from-[#EC4899] via-[#DB2777] to-[#BE185D]' // Pink
        };
      case 'action-items':
        return {
          coloredWord: 'Action',
          restOfName: 'Items',
          titleGradient: 'from-[#F59E0B] via-[#D97706] to-[#B45309]' // Amber/Orange
        };
      case 'meeting-management':
        return {
          coloredWord: 'Meeting',
          restOfName: 'Management',
          titleGradient: 'from-[#F97316] via-[#EA580C] to-[#C2410C]' // Orange (swapped)
        };
      case 'strategy':
        return {
          coloredWord: 'Strategy',
          restOfName: 'Execution',
          titleGradient: 'from-[#14B8A6] via-[#0D9488] to-[#0F766E]' // Teal
        };
      case 'strategy-development':
        return {
          coloredWord: 'Strategy',
          restOfName: 'Development',
          titleGradient: 'from-[#06B6D4] via-[#0891B2] to-[#0E7490]' // Cyan
        };
      default:
        return {
          coloredWord: 'Project',
          restOfName: 'Management', 
          titleGradient: 'from-[#8B5CF6] via-[#7C3AED] to-[#6D28D9]' // Purple
        };
    }
  };
  const moduleConfig = getModuleConfig();

  const isActive = (path, exact = false) => {
    // For exact matching (like /action-items which shouldn't match /action-items/lists)
    if (exact || path === '/dashboard' || path === '/portfolio' || path === '/meetings') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Get the gradient for a button
  const getButtonGradient = (gradientKey) => {
    return currentColorTheme?.buttonGradients?.[gradientKey] || 'from-blue-500 to-blue-600';
  };

  return (
    <div 
      className={`${
        collapsed ? 'w-20' : 'w-64'
      } bg-sidebar border-r border-sidebar-border flex flex-col h-screen fixed left-0 top-0 transition-all duration-300 ease-in-out z-50`}
    >
      {/* Block 1: Strategy Wolves Branding with collapse arrow */}
      <div className="p-4 border-b border-sidebar-border">
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <div className={`flex items-center ${collapsed ? '' : 'gap-3'}`}>
            {/* Logo - Circle Wolf */}
            <img 
              src="/logos/logo-circle-wolf.png" 
              alt="Strategy Wolves" 
              className={collapsed ? 'w-10 h-10' : 'w-12 h-12'}
              style={{ objectFit: 'contain' }}
            />
            {/* Glowing Divider and Stacked Text */}
            {!collapsed && (
              <>
                <div 
                  className="w-px h-10 bg-white/60"
                  style={{ boxShadow: '0 0 8px 2px rgba(255, 255, 255, 0.4)' }}
                />
                <div className="flex flex-col leading-none">
                  <span className={`text-lg font-bold bg-gradient-to-r ${
                    colorTheme === 'midnight-dark' 
                      ? 'from-[#9CA3AF] via-[#D1D5DB] to-[#FFFFFF]' 
                      : 'from-[#7A8A99] via-[#96A4AF] to-[#B0BBC4]'
                  } bg-clip-text text-transparent`}>
                    Strategy
                  </span>
                  <span className="text-lg font-bold text-white/90 -mt-0.5">
                    Wolves
                  </span>
                </div>
              </>
            )}
          </div>
          {/* Collapse Arrow */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={`text-sidebar-foreground/60 hover:bg-sidebar-accent h-8 w-8 ${collapsed ? 'mt-2' : ''}`}
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Block 2: Module Name - Left aligned with logo */}
      <div className="px-4 py-5 border-b border-sidebar-border">
        {!collapsed ? (
          <div className="flex items-center justify-start">
            <h2 className="text-xl font-bold tracking-tight">
              <span 
                className={`bg-gradient-to-r ${moduleConfig.titleGradient} bg-clip-text text-transparent`}
              >
                {moduleConfig.coloredWord}
              </span>
              <span className="text-white ml-2">
                {moduleConfig.restOfName}
              </span>
            </h2>
          </div>
        ) : (
          <div className="flex justify-center">
            <span 
              className={`bg-gradient-to-r ${moduleConfig.titleGradient} bg-clip-text text-transparent text-sm font-bold`}
            >
              {moduleConfig.coloredWord.charAt(0)}{moduleConfig.restOfName.charAt(0)}
            </span>
          </div>
        )}
      </div>

      {/* Block 3: Entity Selector - Organization drill-down (hidden for strategy-development which has its own entity nav) */}
      {organization && moduleType !== 'strategy-development' && (
        <div className="px-3 py-3 border-b border-sidebar-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-sidebar-accent/50 hover:bg-sidebar-accent transition-colors text-left ${collapsed ? 'justify-center' : ''}`}
                data-testid="entity-selector"
              >
                <Building2 className="w-4 h-4 text-sidebar-foreground/70 flex-shrink-0" />
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-sidebar-foreground/50 leading-none mb-0.5">Working on</p>
                    <p className="text-sm font-medium text-sidebar-foreground truncate">
                      {selectedEntity?.name || 'Select Entity'}
                    </p>
                  </div>
                )}
                {!collapsed && <ChevronDown className="w-3 h-3 text-sidebar-foreground/50 flex-shrink-0" />}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="start" className="w-56">
              {/* Corporate */}
              <DropdownMenuItem
                onClick={() => selectEntity({ id: 'corporate', name: organization.corporate_name, type: 'corporate' })}
                className={`cursor-pointer ${selectedEntity?.id === 'corporate' ? 'bg-primary/10' : ''}`}
              >
                <Building2 className="w-4 h-4 mr-2 text-primary" />
                <div>
                  <p className="text-sm font-medium">{organization.corporate_name}</p>
                  <p className="text-xs text-muted-foreground">Corporate</p>
                </div>
                {selectedEntity?.id === 'corporate' && <Check className="w-4 h-4 ml-auto text-primary" />}
              </DropdownMenuItem>

              {/* Subsidiaries */}
              {organization.subsidiaries?.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Subsidiaries</span>
                  </div>
                  {organization.subsidiaries.map(sub => (
                    <DropdownMenuItem
                      key={sub.id}
                      onClick={() => selectEntity({ id: `sub-${sub.id}`, name: sub.name, type: 'subsidiary' })}
                      className={`cursor-pointer pl-6 ${selectedEntity?.id === `sub-${sub.id}` ? 'bg-primary/10' : ''}`}
                    >
                      <span className="text-sm">{sub.name}</span>
                      {selectedEntity?.id === `sub-${sub.id}` && <Check className="w-4 h-4 ml-auto text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {/* Functions */}
              {organization.functions?.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Functions</span>
                  </div>
                  {organization.functions.map(func => (
                    <DropdownMenuItem
                      key={func.id}
                      onClick={() => selectEntity({ id: `func-${func.id}`, name: func.name, type: 'function' })}
                      className={`cursor-pointer pl-6 ${selectedEntity?.id === `func-${func.id}` ? 'bg-primary/10' : ''}`}
                    >
                      <span className="text-sm">{func.name}</span>
                      {selectedEntity?.id === `func-${func.id}` && <Check className="w-4 h-4 ml-auto text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {menuItems.map((item, index) => {
          // Handle divider
          if (item.type === 'divider') {
            return (
              <div key={`divider-${index}`} className="my-2 border-t border-sidebar-border/50" />
            );
          }
          
          // Handle section header
          if (item.type === 'header') {
            if (collapsed) return null;
            return (
              <div key={`header-${index}`} className="pt-4 pb-1 px-2 first:pt-0">
                <span className="text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider">
                  {item.label}
                </span>
              </div>
            );
          }
          
          const Icon = item.icon;
          const gradient = getButtonGradient(item.gradientKey);

          // For entity-action items (strategy-development entity nav)
          if (item.entityAction) {
            const isEntityActive = item.entityAction === 'clear'
              ? !selectedEntity && location.pathname === '/strategy-development'
              : selectedEntity?.id === item.entity?.id;

            return (
              <div key={`entity-${item.entityAction}-${item.entity?.id || 'all'}`} className="relative flex items-center">
                <button
                  onClick={() => {
                    if (item.entityAction === 'clear') {
                      selectEntity(null);
                    } else {
                      selectEntity(item.entity);
                    }
                    if (location.pathname !== '/strategy-development') {
                      navigate('/strategy-development');
                    }
                  }}
                  className={`w-full flex-1 flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                    isEntityActive
                      ? `bg-gradient-to-r ${gradient} text-white shadow-lg`
                      : 'text-sidebar-foreground hover:bg-sidebar-accent'
                  }`}
                  title={collapsed ? item.label : ''}
                  data-testid={`sidebar-entity-${item.entity?.id || 'all'}`}
                >
                  {isEntityActive && (
                    <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                  )}
                  <Icon className={`w-5 h-5 relative z-10 flex-shrink-0 ${isEntityActive ? 'text-white' : ''}`} />
                  {!collapsed && (
                    <span className={`font-medium relative z-10 truncate ${isEntityActive ? 'text-white' : ''}`}>
                      {item.label}
                    </span>
                  )}
                </button>
              </div>
            );
          }
          
          const active = isActive(item.path, item.exact);
          
          return (
            <div key={item.path} className="relative flex items-center">
              <Link
                to={item.path}
                className={`flex-1 flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${
                  active
                    ? `bg-gradient-to-r ${gradient} text-white shadow-lg`
                    : 'text-sidebar-foreground hover:bg-sidebar-accent'
                }`}
                title={collapsed ? item.label : ''}
              >
                {active && (
                  <div className="absolute inset-0 bg-white/10 animate-pulse"></div>
                )}
                <Icon className={`w-5 h-5 relative z-10 ${active ? 'text-white' : ''}`} />
                {!collapsed && (
                  <span className={`font-medium relative z-10 ${active ? 'text-white' : ''}`}>
                    {item.label}
                  </span>
                )}
              </Link>
              {/* Settings icon for items that have settings */}
              {item.hasSettings && !collapsed && (
                <Link
                  to={item.settingsPath}
                  className="absolute right-2 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/50 transition-colors z-20"
                  title={`Manage ${item.label}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Settings className="w-4 h-4" />
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      {/* Back to Modules Block - Separate section above themes */}
      <div className="p-3 border-t border-sidebar-border">
        {!collapsed ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/select-module')}
            className="w-full justify-start text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Modules
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/select-module')}
            className="w-full text-muted-foreground hover:text-foreground"
            title="Back to Modules"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Theme Controls - Single line with switch and color picker */}
      <div className="p-3 border-t border-sidebar-border">
        <div className={`flex items-center ${collapsed ? 'flex-col gap-3' : 'justify-between'}`}>
          {/* Dark/Light Mode Switch */}
          <div className={`flex items-center gap-2 ${collapsed ? '' : ''}`}>
            <Sun className="w-4 h-4 text-sidebar-foreground/60" />
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              className="data-[state=checked]:bg-sidebar-primary"
            />
            <Moon className="w-4 h-4 text-sidebar-foreground/60" />
          </div>

          {/* Color Theme Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-0.5 p-1.5 rounded-lg hover:bg-sidebar-accent transition-colors cursor-pointer"
                title="Change theme colors"
              >
                {currentColorTheme?.preview.map((color, i) => (
                  <div
                    key={i}
                    className="w-4 h-4 first:rounded-l last:rounded-r"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="w-48">
              <DropdownMenuSeparator />
              {Object.entries(colorThemes).map(([key, themeConfig]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => changeColorTheme(key)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-0.5">
                      {themeConfig.preview.map((color, i) => (
                        <div
                          key={i}
                          className="w-4 h-4 first:rounded-l last:rounded-r"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <span className="text-sm">{themeConfig.name}</span>
                  </div>
                  {colorTheme === key && <Check className="w-4 h-4 text-primary" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* User Profile */}
      <div className="p-3 border-t border-sidebar-border">
        {!collapsed ? (
          <>
            <div className="flex items-center space-x-3 p-3 rounded-xl hover:bg-sidebar-accent transition-colors cursor-pointer mb-2">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarImage src={currentUser?.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white">
                  {currentUser?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">{currentUser?.name}</p>
                <p className="text-xs text-sidebar-foreground/60 truncate">{currentUser?.email}</p>
              </div>
            </div>
            <div className="space-y-1">
              <Link
                to="/settings"
                className="flex items-center space-x-3 px-3 py-2 text-sm text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </Link>
              <button
                className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-2">
              <Avatar className="w-10 h-10">
                <AvatarImage src={currentUser?.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-primary to-secondary text-white">
                  {currentUser?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="w-full text-red-400 hover:bg-red-500/10"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
